'use client';

import { useState, useRef, useEffect } from 'react';
import PhoneInput, { getCountryCallingCode } from 'react-phone-number-input';
import type { Country } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { useIsMobile } from '@/hooks/use-mobile';

// Themed country-picker used by every <PhoneInput> on the platform so the
// dropdown matches the surrounding surface (dark organiser dashboard, light
// public registration, etc.). Extracted from ParticipantInfoStep.

interface ThemedCountrySelectProps {
  value?: string;
  onChange: (value: string | undefined) => void;
  options: Array<{ value?: string; label: string; divider?: boolean }>;
  iconComponent: React.ComponentType<{ country: string; label: string }>;
  /** The v8 look (tokens, hairlines, 16px search on a phone). Off: exactly as before. */
  v8?: boolean;
  [key: string]: any;
}

export function ThemedCountrySelect({
  value,
  onChange,
  options,
  iconComponent: Icon,
  v8 = false,
}: ThemedCountrySelectProps) {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (open && searchRef.current) searchRef.current.focus();
  }, [open]);

  const realOptions = options.filter((o) => !o.divider && o.value);
  const filtered = search
    ? realOptions.filter((o) => o.label.toLowerCase().includes(search.toLowerCase()))
    : realOptions;

  const selectedLabel = realOptions.find((o) => o.value === value)?.label || '';

  return (
    <div ref={ref} style={{ position: 'relative', flexShrink: 0 }}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-label={v8 ? `Country: ${selectedLabel || 'none'}` : undefined}
        aria-expanded={v8 ? open : undefined}
        style={v8 ? {
          display: 'flex',
          alignItems: 'center',
          gap: 5,
          minHeight: isMobile ? 36 : 28,
          padding: '0 4px',
          borderRadius: 6,
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          color: 'var(--fvd-ink-muted)',
        } : {
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          padding: '4px 6px',
          borderRadius: '6px',
          border: '1px solid currentColor',
          borderColor: 'inherit',
          background: 'transparent',
          cursor: 'pointer',
          color: 'inherit',
          fontSize: '14px',
          opacity: 0.8,
        }}
        title={selectedLabel}
      >
        {value && <Icon country={value} label={selectedLabel} />}
        <svg width="10" height="6" viewBox="0 0 10 6" fill="none" style={{ opacity: 0.5 }}>
          <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div style={v8 ? {
          position: 'absolute',
          top: '100%',
          left: -8,
          marginTop: 8,
          zIndex: 50,
          width: 260,
          maxWidth: 'calc(100vw - 32px)',
          maxHeight: 300,
          borderRadius: 'var(--fvd-radius)',
          border: '1px solid var(--fvd-hairline-strong)',
          background: 'var(--fvd-elevated, var(--fvd-canvas))',
          boxShadow: 'var(--fvd-shadow-dropdown)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          color: 'var(--fvd-ink)',
          fontFamily: 'var(--fvd-font-body)',
        } : {
          position: 'absolute',
          top: '100%',
          left: 0,
          marginTop: '4px',
          zIndex: 50,
          width: '260px',
          maxHeight: '280px',
          borderRadius: '8px',
          border: '1px solid rgba(128,128,128,0.3)',
          background: 'var(--fv-bg-overlay, #fff)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          color: 'inherit',
        }}>
          <div style={{ padding: '8px', borderBottom: v8 ? '1px solid var(--fvd-hairline)' : '1px solid rgba(128,128,128,0.2)' }}>
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={v8 ? 'Search countries' : 'Search country...'}
              aria-label={v8 ? 'Search countries' : undefined}
              style={v8 ? {
                width: '100%',
                boxSizing: 'border-box',
                minHeight: isMobile ? 44 : 32,
                padding: '6px 10px',
                border: '1px solid var(--fvd-border-control)',
                borderRadius: 'var(--fvd-radius)',
                background: 'transparent',
                color: 'var(--fvd-ink)',
                fontFamily: 'inherit',
                fontSize: isMobile ? 16 : 13,
                outline: 'none',
              } : {
                width: '100%',
                padding: '6px 10px',
                border: '1px solid rgba(128,128,128,0.3)',
                borderRadius: '6px',
                background: 'transparent',
                color: 'inherit',
                fontSize: '13px',
                outline: 'none',
              }}
            />
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {filtered.map((option) => (
              <button
                key={option.value || 'intl'}
                type="button"
                onClick={() => {
                  onChange(option.value || undefined);
                  setOpen(false);
                  setSearch('');
                }}
                className={v8 ? 'fvd-menu-item' : undefined}
                style={v8 ? {
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  width: '100%',
                  minHeight: isMobile ? 44 : 34,
                  padding: '6px 12px',
                  border: 'none',
                  background: value === option.value ? 'var(--fvd-selected-fill)' : 'transparent',
                  color: 'var(--fvd-ink)',
                  fontFamily: 'inherit',
                  fontSize: 13,
                  fontWeight: value === option.value ? 550 : 400,
                  textAlign: 'left',
                  cursor: 'pointer',
                } : {
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  width: '100%',
                  // 44px min-height meets iOS HIG for touch targets in the
                  // country picker; country rows used to be 36px and easy to
                  // miss on a small device.
                  minHeight: '44px',
                  padding: '8px 12px',
                  border: 'none',
                  background: value === option.value ? 'rgba(128,128,128,0.12)' : 'transparent',
                  color: 'inherit',
                  fontSize: '14px',
                  textAlign: 'left',
                  cursor: 'pointer',
                  WebkitTapHighlightColor: 'rgba(128,128,128,0.25)',
                  transition: 'background 0.15s ease',
                }}
                onMouseEnter={v8 ? undefined : (e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(128,128,128,0.08)'; }}
                onMouseLeave={v8 ? undefined : (e) => { (e.currentTarget as HTMLElement).style.background = value === option.value ? 'rgba(128,128,128,0.12)' : 'transparent'; }}
                onPointerDown={v8 ? undefined : (e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(128,128,128,0.18)'; }}
              >
                {option.value && <Icon country={option.value} label={option.label} />}
                <span style={{ flex: 1 }}>{option.label}</span>
                {option.value && (() => {
                  try { return <span style={v8 ? { color: 'var(--fvd-ink-muted)', fontFamily: 'var(--fvd-font-mono)', fontSize: 11.5 } : { opacity: 0.5, fontSize: '11px' }}>+{getCountryCallingCode(option.value as Country)}</span>; }
                  catch { return null; }
                })()}
              </button>
            ))}
            {filtered.length === 0 && (
              <div style={v8 ? { padding: 12, textAlign: 'center', color: 'var(--fvd-ink-muted)', fontSize: 13 } : { padding: '12px', textAlign: 'center', opacity: 0.5, fontSize: '13px' }}>
                No countries found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

interface PhoneInputFieldProps {
  /** Stored value, expected in E.164 format (`+353851234567`). */
  value: string | null | undefined;
  /** Always emits E.164 (or undefined for empty). Persist this string. */
  onChange: (e164: string | undefined) => void;
  defaultCountry?: Country;
  placeholder?: string;
  className?: string;
  style?: React.CSSProperties;
  disabled?: boolean;
  /**
   * The v8 look (athlete hub, emailed ticket page): a hairline field in the
   * --fvd-* tokens, 44px and 16px on a phone, and the country list drawn as a
   * v8 menu. Off, the field is exactly what every other caller has today.
   */
  v8?: boolean;
}

/** The country list in the v8 look. A component of its own so the widget can render it. */
function V8CountrySelect(props: ThemedCountrySelectProps) {
  return <ThemedCountrySelect {...props} v8 />;
}

/**
 * The v8 field's rules. In a style tag rather than globals.css, where
 * Tailwind v4 has dropped hand-written rules from the build; and not under
 * .fv-phone-input, whose !important border is the old token's.
 */
const V8_PHONE_CSS = `
.fvd-phone-input.PhoneInput { display: flex; align-items: center; gap: 6px; width: 100%; box-sizing: border-box; padding: 0 12px 0 8px; border: 1px solid var(--fvd-border-control); border-radius: var(--fvd-radius); background: transparent; color: var(--fvd-ink); font-family: inherit; }
.fvd-phone-input.PhoneInput:focus-within { border-color: var(--fvd-teal); }
.fvd-phone-input .PhoneInputCountry { display: flex; align-items: center; margin: 0; }
.fvd-phone-input .PhoneInputCountryIcon { width: 22px; height: 16px; border-radius: 2px; overflow: hidden; box-shadow: none; background: none; }
.fvd-phone-input .PhoneInputInput { flex: 1; min-width: 0; align-self: stretch; border: none; outline: none; background: transparent; color: inherit; font: inherit; padding: 0; }
.fvd-phone-input .PhoneInputInput::placeholder { color: var(--fvd-ink-muted); }
.fvd-phone-input.PhoneInput--disabled { background: var(--fvd-neutral-chip); color: var(--fvd-ink-secondary); }
`;

/**
 * Country-aware phone input wrapping react-phone-number-input. Use this
 * everywhere a phone number is captured: the underlying widget enforces
 * E.164 storage so display-side formatters (formatPhoneNumberIntl etc.)
 * can render the number correctly per the chosen country, instead of the
 * regex-guesswork that used to mangle bare `085 …` input.
 */
export function PhoneInputField({
  value,
  onChange,
  defaultCountry = 'IE',
  placeholder = '+353 87 123 4567',
  className,
  style,
  disabled,
  v8 = false,
}: PhoneInputFieldProps) {
  const isMobile = useIsMobile();
  if (v8) {
    return (
      <>
        <style dangerouslySetInnerHTML={{ __html: V8_PHONE_CSS }} />
        <PhoneInput
          international
          defaultCountry={defaultCountry}
          value={value || ''}
          onChange={(v: string | undefined) => onChange(v || undefined)}
          placeholder={placeholder}
          className={`fvd-phone-input${className ? ` ${className}` : ''}`}
          style={{ minHeight: isMobile ? 44 : 36, fontSize: isMobile ? 16 : 13, ...style }}
          disabled={disabled}
          countrySelectComponent={V8CountrySelect}
        />
      </>
    );
  }
  return (
    <PhoneInput
      international
      defaultCountry={defaultCountry}
      value={value || ''}
      onChange={(v: string | undefined) => onChange(v || undefined)}
      placeholder={placeholder}
      className={`fv-phone-input${className ? ` ${className}` : ''}`}
      style={style}
      disabled={disabled}
      countrySelectComponent={ThemedCountrySelect}
    />
  );
}
